const BUNDLE_DESKTOP_MQ = '(min-width: 751px)';

if (!customElements.get('product-bundle-sets')) {
  customElements.define(
    'product-bundle-sets',
    class ProductBundleSets extends HTMLElement {
      connectedCallback() {
        this.selectableTotal = parseInt(this.dataset.selectableTotal, 10) || 0;
        this.progressCountEl = this.querySelector('[data-bundle-progress-count]');
        this.progressBarEl = this.querySelector('[data-bundle-progress-bar]');
        this.progressFillEl = this.querySelector('[data-bundle-progress-fill]');
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('submit', this.onBundleFormSubmit.bind(this), true);

        this.querySelectorAll('[data-bundle-dialog]').forEach((dialog) => {
          dialog.addEventListener('close', () => {
            const active = document.activeElement;
            if (active && dialog.contains(active)) {
              const opener = this.querySelector(
                `[data-bundle-dialog-id="${dialog.id}"]`
              );
              opener?.focus?.();
            }
          });
        });

        this.parentVariantConfig = this.getParentVariantConfig();
        this.refreshStripSwatchAvailability();
        this.refreshProgress();
      }

      getCustomMapFromScript() {
        const script = this.querySelector('[data-bundle-custom-variant-map]');
        if (!script?.textContent?.trim()) return {};

        try {
          const parsed = JSON.parse(script.textContent);
          return this.getCustomMap({ customMap: parsed });
        } catch (e) {
          console.warn(
            '[product-bundle-sets] Invalid custom bundle variant map JSON',
            e
          );
          return {};
        }
      }

      getParentVariantConfig() {
        const script = this.querySelector('[data-bundle-parent-variant-map]');
        if (!script) return null;
        try {
          const config = JSON.parse(script.textContent);
          config.customMap = this.getCustomMapFromScript();
          return config;
        } catch (e) {
          console.warn('[product-bundle-sets] Invalid parent variant map JSON', e);
          return null;
        }
      }

      getRowSelections() {
        const selections = [];
        this.querySelectorAll('[data-bundle-row]').forEach((row) => {
          const productId = row.dataset.productId;
          const needsSelection = row.getAttribute('data-needs-selection') === 'true';
          const variantId = needsSelection
            ? row.dataset.committedVariantId
            : row.dataset.defaultVariantId;
          const optionValue = needsSelection
            ? row.dataset.committedOptionValue
            : null;
          const slotIndex = parseInt(row.dataset.bundleSlotIndex, 10);
          const lineIndex = parseInt(row.dataset.bundleLineIndex, 10);
          const unitIndex = parseInt(row.dataset.bundleUnitIndex, 10);
          const parentOptionIndex = parseInt(
            row.dataset.bundleParentOptionIndex,
            10
          );

          if (!productId || !variantId) return;

          selections.push({
            productId,
            variantId,
            optionValue: optionValue || null,
            slotIndex: Number.isNaN(slotIndex) ? null : slotIndex,
            lineIndex: Number.isNaN(lineIndex) ? null : lineIndex,
            unitIndex: Number.isNaN(unitIndex) ? null : unitIndex,
            parentOptionIndex: Number.isNaN(parentOptionIndex)
              ? null
              : parentOptionIndex,
          });
        });
        return selections;
      }

      buildCustomMapKey(selections) {
        return [...selections]
          .filter(
            (sel) => sel.slotIndex != null && sel.parentOptionIndex != null
          )
          .sort((a, b) => a.slotIndex - b.slotIndex)
          .map((sel) => `slot${sel.slotIndex}:${sel.productId}:${sel.variantId}`)
          .join('|');
      }

      buildLegacyCustomMapKey(selections) {
        return selections
          .map((sel) => `${sel.productId}:${sel.variantId}`)
          .sort()
          .join(',');
      }

      isBundleSelectionComplete() {
        return (
          this.selectableTotal <= 0 ||
          this.committedSelectableCount() >= this.selectableTotal
        );
      }

      normalizeOptionValue(value) {
        if (value == null) return null;
        return String(value).trim().toLowerCase();
      }

      optionValuesMatch(actual, expected) {
        return (
          this.normalizeOptionValue(actual) ===
          this.normalizeOptionValue(expected)
        );
      }

      parseParentVariantId(value) {
        if (value == null) return null;
        if (typeof value === 'number' && Number.isFinite(value)) {
          return value;
        }

        const str = String(value).trim();
        if (!str) return null;

        const gidMatch = str.match(/ProductVariant\/(\d+)/i);
        if (gidMatch) {
          const gidId = parseInt(gidMatch[1], 10);
          return Number.isFinite(gidId) ? gidId : null;
        }

        const parsed = parseInt(str, 10);
        return Number.isFinite(parsed) ? parsed : null;
      }

      getCustomMap(config) {
        let raw = config?.customMap;
        if (typeof raw === 'string') {
          try {
            raw = JSON.parse(raw);
          } catch (e) {
            return {};
          }
        }
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          return raw;
        }
        return {};
      }

      getDefaultParentVariantId(config) {
        const variants = config?.parentVariants || [];
        const fromDefault = this.parseParentVariantId(config?.defaultParentVariantId);
        if (fromDefault != null) return fromDefault;

        if (variants.length > 0) {
          return this.parseParentVariantId(variants[0].id);
        }

        return null;
      }

      resolveParentVariant() {
        this._lastResolveFailureReason = null;
        const config = this.parentVariantConfig || this.getParentVariantConfig();

        if (!config) {
          this._lastResolveFailureReason = 'missing_config';
          return null;
        }

        if (!this.isBundleSelectionComplete()) {
          this._lastResolveFailureReason = 'selection_incomplete';
          return null;
        }

        const selections = this.getRowSelections();
        if (!selections.length) {
          this._lastResolveFailureReason = 'no_selections';
          return null;
        }

        const variants = config.parentVariants || [];
        const customMap = this.getCustomMap(config);

        if (Object.keys(customMap).length) {
          const slotKey = this.buildCustomMapKey(selections);
          const legacyKey = this.buildLegacyCustomMapKey(selections);
          const mapped = customMap[slotKey] ?? customMap[legacyKey];
          if (mapped != null) {
            const mappedId = this.parseParentVariantId(mapped);
            if (mappedId != null) return mappedId;
            this._lastResolveFailureReason = 'custom_map_invalid_value';
          }
        }

        if (variants.length === 1) {
          const singleId = this.parseParentVariantId(variants[0].id);
          if (singleId != null) return singleId;
        }

        const parentOptions = {};
        selections.forEach((sel) => {
          if (sel.parentOptionIndex != null && sel.optionValue) {
            parentOptions[sel.parentOptionIndex] = sel.optionValue;
          }
        });

        const hasParentOptionValues = Object.keys(parentOptions).length > 0;

        if (hasParentOptionValues) {
          const match = variants.find((variant) => {
            for (let i = 1; i <= 3; i += 1) {
              const expected = parentOptions[i];
              if (expected == null) continue;
              if (!this.optionValuesMatch(variant[`option${i}`], expected)) {
                return false;
              }
            }
            return true;
          });
          if (match) {
            const matchedId = this.parseParentVariantId(match.id);
            if (matchedId != null) return matchedId;
          }
        }

        const fallbackId = this.getDefaultParentVariantId(config);
        if (fallbackId != null) return fallbackId;

        this._lastResolveFailureReason =
          this._lastResolveFailureReason || 'no_parent_variant_match';
        return null;
      }

      getResolveFailureReason() {
        if (this._lastResolveFailureReason) {
          return this._lastResolveFailureReason;
        }
        if (!this.parentVariantConfig && !this.getParentVariantConfig()) {
          return 'missing_config';
        }
        return null;
      }

      applyResolvedParentVariant() {
        const parentId = this.resolveParentVariant();
        const input = this.getBundleProductForm()?.querySelector('.product-variant-id');
        if (!input) return parentId;

        if (parentId) {
          input.value = String(parentId);
          input.removeAttribute('disabled');
        }

        return parentId;
      }

      getResolvedParentVariantId() {
        return this.resolveParentVariant();
      }

      getBundleProductForm() {
        return this.querySelector('[data-bundle-cta] product-form');
      }

      getBundleSubmitButton() {
        return this.getBundleProductForm()?.querySelector(
          'button.product-form__submit[name="add"]'
        );
      }

      getMainVariantId() {
        const input = this.getBundleProductForm()?.querySelector('.product-variant-id');
        if (!input?.value || input.disabled) return null;
        return input.value;
      }

      isParentVariantSellable() {
        const input = this.getBundleProductForm()?.querySelector('.product-variant-id');
        return Boolean(input?.value && !input.disabled);
      }

      onBundleFormSubmit(event) {
        const form = event.target;
        if (!form?.matches?.('[data-type="add-to-cart-form"]')) return;
        if (!this.contains(form)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const submitBtn = this.getBundleSubmitButton();
        if (!submitBtn || submitBtn.disabled) return;
        if (submitBtn.getAttribute('aria-disabled') === 'true') return;
        if (!this.canAddBundleToCart()) return;

        this.addBundleToCart();
      }

      onClick(event) {
        const openBtn = event.target.closest('[data-bundle-open-dialog]');
        if (openBtn) {
          const id = openBtn.getAttribute('data-bundle-dialog-id');
          const dialog = id ? document.getElementById(id) : null;
          if (dialog && dialog.tagName === 'DIALOG') {
            const row = openBtn.closest('[data-bundle-row]');
            const committed = row?.dataset?.committedVariantId;
            if (committed) {
              this.setDialogFromVariantId(dialog, committed);
            }
            if (row) {
              this.clearDuplicateShadeError(row);
              this.updateStripSwatchAvailability(row);
            }
            this.updateDialogPreview(dialog);
            this.updateDialogConfirmState(dialog);
            dialog.showModal();
            this.trapFocus(dialog);
          }
          return;
        }

        const closeBtn = event.target.closest('[data-bundle-dialog-close]');
        if (closeBtn) {
          const dialog = closeBtn.closest('dialog');
          dialog?.close();
          return;
        }

        const confirmBtn = event.target.closest('[data-bundle-confirm]');
        if (confirmBtn) {
          const dialog = confirmBtn.closest('[data-bundle-dialog]');
          if (dialog) this.confirmDialog(dialog);
          return;
        }

        const swatchPick = event.target.closest('[data-bundle-swatch-pick]');
        if (swatchPick && window.matchMedia(BUNDLE_DESKTOP_MQ).matches) {
          this.pickStripSwatch(swatchPick);
        }
      }

      onChange(event) {
        const stripSwatchPick = event.target.closest('[data-bundle-swatch-pick]');
        if (stripSwatchPick && stripSwatchPick.tagName === 'INPUT') {
          const inDialog = stripSwatchPick.closest('[data-bundle-dialog]');
          if (!inDialog) {
            this.pickStripSwatch(stripSwatchPick);
          }
        }

        const dialog = event.target.closest('[data-bundle-dialog]');
        if (!dialog) return;
        this.syncPickerUi(dialog, event.target);
        this.updateDialogPreview(dialog);
        this.updateDialogConfirmState(dialog);
      }

      getVariants(dialog) {
        const script = dialog.querySelector('[data-bundle-variants]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent);
        } catch (e) {
          return [];
        }
      }

      getSelectedOptionsFromDialog(dialog) {
        const out = {};
        dialog.querySelectorAll('[data-option-position]').forEach((el) => {
          const pos = parseInt(el.dataset.optionPosition, 10);
          if (Number.isNaN(pos)) return;

          if (el.tagName === 'FIELDSET') {
            const checked = el.querySelector('input[type="radio"]:checked');
            if (checked) out[pos] = checked.value;
          } else if (el.classList.contains('product-form__input--dropdown')) {
            const sel = el.querySelector('select');
            if (sel?.selectedOptions?.length) out[pos] = sel.selectedOptions[0].value;
          }
        });

        const previewWrap = dialog.querySelector('[data-bundle-preview-swatches]');
        if (previewWrap) {
          const pos = parseInt(previewWrap.dataset.stripOptionPosition, 10);
          const checked = previewWrap.querySelector(
            '[data-bundle-swatch-pick]:checked'
          );
          if (!Number.isNaN(pos) && checked) {
            out[pos] = checked.value;
          }
        }

        return out;
      }

      findVariantForOptions(variants, selectedByPosition) {
        const matches = variants.filter((v) => {
          for (let i = 1; i <= 3; i += 1) {
            const sel = selectedByPosition[i];
            if (sel == null) continue;
            const key = `option${i}`;
            if (v[key] !== sel) return false;
          }
          return true;
        });
        if (!matches.length) return null;
        return matches.find((v) => v.available) || matches[0];
      }

      findAvailableVariantForOptions(variants, selectedByPosition) {
        const matches = variants.filter((v) => {
          for (let i = 1; i <= 3; i += 1) {
            const sel = selectedByPosition[i];
            if (sel == null) continue;
            const key = `option${i}`;
            if (v[key] !== sel) return false;
          }
          return true;
        });
        return matches.find((v) => v.available) || null;
      }

      findVariantsForStripOption(variants, selectedByPosition, stripPosition, stripValue) {
        return variants.filter((v) => {
          if (v[`option${stripPosition}`] !== stripValue) return false;
          for (let i = 1; i <= 3; i += 1) {
            const sel = selectedByPosition[i];
            if (sel == null) continue;
            if (v[`option${i}`] !== sel) return false;
          }
          return true;
        });
      }

      getStripSwatchState(variants, selectedByPosition, stripPosition, stripValue) {
        const matches = this.findVariantsForStripOption(
          variants,
          selectedByPosition,
          stripPosition,
          stripValue
        );
        const hasAvailable = matches.some((v) => v.available);
        const representative = matches.find((v) => v.available) || matches[0];
        const visuallyDisabled =
          !hasAvailable || Boolean(representative?.swatch_unavailable);
        return { visuallyDisabled, pickable: hasAvailable };
      }

      setSwatchInputVisuallyDisabled(input, isVisuallyDisabled) {
        if (!input || input.type !== 'radio') return;
        input.classList.toggle('visually-disabled', Boolean(isVisuallyDisabled));
      }

      setBundleSwatchPickSelected(pickInput, isSelected) {
        if (!pickInput) return;
        pickInput.checked = isSelected;
        pickInput.toggleAttribute('data-bundle-selected', isSelected);
      }

      getSwatchFromBundlePick(pickInput) {
        return pickInput?.nextElementSibling?.querySelector('.swatch') ?? null;
      }

      getBundlePickByValue(container, value) {
        if (!container || value == null) return null;
        return container.querySelector(
          `[data-bundle-swatch-pick][data-preview-value="${CSS.escape(value)}"]`
        );
      }

      getOptionsFromVariant(variant) {
        const out = {};
        if (!variant) return out;
        for (let i = 1; i <= 3; i += 1) {
          const val = variant[`option${i}`];
          if (val) out[i] = val;
        }
        return out;
      }

      getRowDialog(row) {
        return row.querySelector('[data-bundle-dialog]');
      }

      getRowForDialog(dialog) {
        if (!dialog) return null;
        const slotIndex = dialog.dataset.bundleSlotIndex;
        if (slotIndex) {
          return this.querySelector(
            `[data-bundle-row][data-bundle-slot-index="${slotIndex}"]`
          );
        }
        return dialog.closest('[data-bundle-row]');
      }

      getSiblingRowsSameProduct(row) {
        if (!row?.dataset?.productId) return [];
        const productId = row.dataset.productId;
        return Array.from(
          this.querySelectorAll(`[data-bundle-row][data-product-id="${productId}"]`)
        ).filter((sibling) => sibling !== row);
      }

      isVariantCommittedOnSibling(row, variantId) {
        if (row == null || variantId == null) return false;
        const variantKey = String(variantId);
        return this.getSiblingRowsSameProduct(row).some(
          (sibling) => sibling.dataset.committedVariantId === variantKey
        );
      }

      getCommittedVariantIdsForProduct(productId, excludeRow = null) {
        if (!productId) return new Set();
        const committed = new Set();
        this.querySelectorAll(
          `[data-bundle-row][data-product-id="${productId}"]`
        ).forEach((row) => {
          if (row === excludeRow) return;
          const variantId = row.dataset.committedVariantId;
          if (variantId) committed.add(variantId);
        });
        return committed;
      }

      showDuplicateShadeError(row) {
        const errorEl = row?.querySelector('[data-bundle-duplicate-error]');
        if (!errorEl) return;
        errorEl.hidden = false;
      }

      clearDuplicateShadeError(row) {
        const errorEl = row?.querySelector('[data-bundle-duplicate-error]');
        if (!errorEl) return;
        errorEl.hidden = true;
      }

      getRowPreviewSwatchWraps(row) {
        return row.querySelectorAll('[data-bundle-preview-swatches]');
      }

      buildSelectedForStripPick(row, dialog, stripPosition, stripValue) {
        const variants = this.getVariants(dialog);
        const defaultId = row.dataset.defaultVariantId;
        const baseVariant =
          variants.find((v) => String(v.id) === String(defaultId)) || variants[0];
        const selected = this.getOptionsFromVariant(baseVariant);
        selected[stripPosition] = stripValue;
        return selected;
      }

      pickStripSwatch(swatchPick) {
        if (swatchPick.disabled) return;

        const row = swatchPick.closest('[data-bundle-row]');
        const wrap = swatchPick.closest('[data-bundle-preview-swatches]');
        const dialog = row ? this.getRowDialog(row) : null;
        if (!row || !wrap || !dialog) return;

        const stripPosition = parseInt(wrap.dataset.stripOptionPosition, 10);
        const stripValue = swatchPick.dataset.previewValue;
        if (Number.isNaN(stripPosition) || stripValue == null) return;

        const variants = this.getVariants(dialog);
        const selected = this.buildSelectedForStripPick(
          row,
          dialog,
          stripPosition,
          stripValue
        );
        const variant = this.findAvailableVariantForOptions(variants, selected);
        if (!variant) return;

        if (this.isVariantCommittedOnSibling(row, variant.id)) {
          this.showDuplicateShadeError(row);
          return;
        }

        this.commitRowSelection(row, variant, selected, dialog);
      }

      refreshStripSwatchAvailability() {
        this.querySelectorAll(
          '[data-bundle-row][data-needs-selection="true"]'
        ).forEach((row) => {
          this.updateStripSwatchAvailability(row);
        });
      }

      updateStripSwatchAvailability(row) {
        const dialog = this.getRowDialog(row);
        if (!dialog) return;

        const variants = this.getVariants(dialog);

        this.getRowPreviewSwatchWraps(row).forEach((wrap) => {
          const stripPosition = parseInt(wrap.dataset.stripOptionPosition, 10);
          if (Number.isNaN(stripPosition)) return;

          const siblingCommittedIds = this.getCommittedVariantIdsForProduct(
            row.dataset.productId,
            row
          );

          wrap.querySelectorAll('[data-bundle-swatch-pick]').forEach((btn) => {
            const stripValue = btn.dataset.previewValue;
            const selected = this.buildSelectedForStripPick(
              row,
              dialog,
              stripPosition,
              stripValue
            );
            const { visuallyDisabled, pickable } = this.getStripSwatchState(
              variants,
              selected,
              stripPosition,
              stripValue
            );
            const matchingVariant = this.findAvailableVariantForOptions(
              variants,
              selected
            );
            const duplicateSibling =
              matchingVariant != null &&
              siblingCommittedIds.has(String(matchingVariant.id));

            btn.disabled = !pickable || duplicateSibling;
            this.setSwatchInputVisuallyDisabled(
              btn,
              visuallyDisabled || duplicateSibling
            );

            if (!pickable || duplicateSibling) {
              this.setBundleSwatchPickSelected(btn, false);
            }
          });
        });
      }

      getCommittedOptionValue(row, selectedByPosition, variant, dialog) {
        const wrap = row.querySelector('[data-bundle-preview-swatches]');
        if (wrap) {
          const stripPosition = parseInt(wrap.dataset.stripOptionPosition, 10);
          if (!Number.isNaN(stripPosition)) {
            const val =
              selectedByPosition[stripPosition] ?? variant?.[`option${stripPosition}`];
            if (val != null) return val;
          }
        }

        if (dialog) {
          const previewWrap = dialog.querySelector('[data-bundle-preview-swatches]');
          if (previewWrap) {
            const stripPosition = parseInt(
              previewWrap.dataset.stripOptionPosition,
              10
            );
            if (!Number.isNaN(stripPosition)) {
              const val =
                selectedByPosition[stripPosition] ??
                variant?.[`option${stripPosition}`];
              if (val != null) return val;
            }
          }
        }

        return variant?.title && variant.title !== 'Default Title'
          ? variant.title
          : null;
      }

      commitRowSelection(row, variant, selectedByPosition, dialog) {
        if (this.isVariantCommittedOnSibling(row, variant.id)) {
          this.showDuplicateShadeError(row);
          return false;
        }

        this.clearDuplicateShadeError(row);
        row.dataset.committedVariantId = String(variant.id);

        const optionValue = this.getCommittedOptionValue(
          row,
          selectedByPosition,
          variant,
          dialog
        );
        if (optionValue != null) {
          row.dataset.committedOptionValue = optionValue;
        }

        const actionBtn = row.querySelector('[data-bundle-row-action]');
        if (actionBtn) actionBtn.textContent = 'Change';

        if (dialog) {
          this.setDialogFromVariantId(dialog, variant.id);
        }

        this.updateRowSelectionPreview(row, selectedByPosition, variant, dialog);
        this.syncStripSelectedState(row, selectedByPosition);
        this.refreshStripSwatchAvailability();
        this.refreshProgress();
        return true;
      }

      syncStripSelectedState(row, selectedByPosition) {
        this.getRowPreviewSwatchWraps(row).forEach((wrap) => {
          const pos = parseInt(wrap.dataset.stripOptionPosition, 10);
          const selectedValue = Number.isNaN(pos) ? null : selectedByPosition[pos];

          wrap.querySelectorAll('[data-bundle-swatch-pick]').forEach((btn) => {
            const isSelected =
              selectedValue != null && btn.dataset.previewValue === selectedValue;
            this.setBundleSwatchPickSelected(btn, isSelected);
          });
        });
      }

      setDialogFromVariantId(dialog, variantId) {
        const variants = this.getVariants(dialog);
        const v = variants.find((x) => String(x.id) === String(variantId));
        if (!v) return;

        for (let i = 1; i <= 3; i += 1) {
          const val = v[`option${i}`];
          if (!val) continue;
          const holder = dialog.querySelector(`[data-option-position="${i}"]`);
          if (!holder) continue;

          if (holder.tagName === 'FIELDSET') {
            const inputs = holder.querySelectorAll('input[type="radio"]');
            inputs.forEach((inp) => {
              inp.checked = inp.value === val;
              if (inp.checked) {
                this.syncPickerUi(dialog, inp);
              }
            });
          } else if (holder.classList.contains('product-form__input--dropdown')) {
            const sel = holder.querySelector('select');
            if (sel) {
              sel.value = val;
              this.syncPickerUi(dialog, sel);
            }
          }
        }

        const row = this.getRowForDialog(dialog);
        if (!row) return;

        this.getRowPreviewSwatchWraps(row).forEach((wrap) => {
          const pos = parseInt(wrap.dataset.stripOptionPosition, 10);
          if (Number.isNaN(pos)) return;

          const selectedValue = v[`option${pos}`];
          wrap.querySelectorAll('[data-bundle-swatch-pick]').forEach((btn) => {
            const isSelected =
              selectedValue != null && btn.dataset.previewValue === selectedValue;
            this.setBundleSwatchPickSelected(btn, isSelected);
          });
        });
      }

      syncPickerUi(dialog, target) {
        const tag = target.tagName;
        if (tag === 'SELECT') {
          const wrap = target.closest('.product-form__input--dropdown');
          if (!wrap) return;
          const opt = target.selectedOptions[0];
          const swatchValue = opt?.dataset?.optionSwatchValue;
          const focal = opt?.dataset?.optionSwatchFocalPoint;
          const dropdownSwatch = wrap.querySelector('[data-selected-value] > .swatch');
          if (dropdownSwatch && typeof applySwatchStyles === 'function') {
            if (swatchValue) {
              dropdownSwatch.style.setProperty('--swatch--background', swatchValue);
            } else {
              dropdownSwatch.style.setProperty('--swatch--background', 'transparent');
            }
            dropdownSwatch.style.setProperty('--swatch-focal-point', focal || 'unset');
          }
          return;
        }

        if (tag === 'INPUT' && target.type === 'radio') {
          const fieldset = target.closest('.product-form__input');
          if (!fieldset) return;
          const selectedSwatchLabelText = fieldset.querySelector(
            '[data-selected-swatch-label-text]'
          );
          if (selectedSwatchLabelText) {
            const labelText = target.dataset.optionSwatchLabelText || target.value;
            selectedSwatchLabelText.textContent = labelText;
          }
          const selectedPillValue = fieldset.querySelector('[data-selected-pill-value]');
          if (selectedPillValue) {
            selectedPillValue.textContent = target.value;
          }
          const selectedSwatchEl = fieldset.querySelector('[data-selected-value] > .swatch');
          const sourceSwatch =
            target.nextElementSibling?.querySelector('.swatch') || selectedSwatchEl;
          if (
            selectedSwatchEl &&
            sourceSwatch &&
            typeof applySwatchStyles === 'function'
          ) {
            applySwatchStyles(sourceSwatch, selectedSwatchEl);
          }
        }
      }

      updateDialogSummary(dialog, variant, selectedByPosition) {
        const summary = dialog.querySelector('[data-bundle-dialog-summary]');
        if (!summary) return;

        const summaryLabel = summary.querySelector('[data-bundle-dialog-summary-label]');
        const summarySwatch = summary.querySelector(
          '[data-bundle-dialog-summary-swatch] .swatch'
        );
        const optionPosition = parseInt(
          summary.dataset.bundleSummaryOptionPosition,
          10
        );

        if (!Number.isNaN(optionPosition) && summaryLabel) {
          const selectedValue = selectedByPosition[optionPosition];
          if (selectedValue != null) {
            summaryLabel.textContent = selectedValue;
            let sourceSwatch = null;
            const dialogWrap = dialog.querySelector(
              '[data-bundle-preview-swatches]'
            );
            const pick =
              this.getBundlePickByValue(dialogWrap, selectedValue) ||
              dialogWrap?.querySelector('[data-bundle-swatch-pick]:checked');
            sourceSwatch = this.getSwatchFromBundlePick(pick);

            if (!sourceSwatch) {
              const holder = dialog.querySelector(
                `[data-option-position="${optionPosition}"]`
              );
              if (holder?.tagName === 'FIELDSET') {
                const checked = holder.querySelector('input[type="radio"]:checked');
                sourceSwatch =
                  checked?.nextElementSibling?.querySelector('.swatch') ||
                  holder.querySelector('[data-selected-value] > .swatch');
              } else if (
                holder?.classList.contains('product-form__input--dropdown')
              ) {
                sourceSwatch = holder.querySelector('[data-selected-value] > .swatch');
              }
            }

            if (
              summarySwatch &&
              sourceSwatch &&
              typeof applySwatchStyles === 'function'
            ) {
              applySwatchStyles(sourceSwatch, summarySwatch);
            }
            return;
          }
        }

        if (variant && summaryLabel) {
          summaryLabel.textContent = variant.title;
        }
      }

      updateDialogPreview(dialog) {
        const variants = this.getVariants(dialog);
        const selected = this.getSelectedOptionsFromDialog(dialog);
        const variant = this.findVariantForOptions(variants, selected);
        const img = dialog.querySelector('[data-bundle-dialog-image]');
        const row = this.getRowForDialog(dialog);

        this.updateDialogSummary(dialog, variant, selected);

        if (img && img.tagName === 'IMG') {
          const featured = variant?.featured_image;
          const src = featured?.src || row?.dataset?.fallbackImage;
          if (src) img.setAttribute('src', src);
        }
      }

      confirmDialog(dialog) {
        const confirmBtn = dialog.querySelector('[data-bundle-confirm]');
        if (confirmBtn?.disabled) return;

        const variants = this.getVariants(dialog);
        const selected = this.getSelectedOptionsFromDialog(dialog);
        const variant = this.findAvailableVariantForOptions(variants, selected);
        if (!variant) return;

        const row = this.getRowForDialog(dialog);
        if (!row) return;

        const committed = this.commitRowSelection(row, variant, selected, dialog);
        if (!committed) return;
        dialog.close();
      }

      updateDialogConfirmState(dialog) {
        const confirmBtn = dialog.querySelector('[data-bundle-confirm]');
        if (!confirmBtn) return;
        const checked = dialog.querySelector(
          '.product-bundle-sets__dialog-options [data-bundle-swatch-pick]:checked'
        );
        let duplicate = false;
        const row = this.getRowForDialog(dialog);
        if (checked && row) {
          const variants = this.getVariants(dialog);
          const selected = this.getSelectedOptionsFromDialog(dialog);
          const variant = this.findAvailableVariantForOptions(variants, selected);
          duplicate =
            variant != null && this.isVariantCommittedOnSibling(row, variant.id);
        }
        confirmBtn.disabled = !checked || duplicate;
      }

      updateRowSelectionPreview(row, selectedByPosition, variant, dialog) {
        const wrap = row.querySelector('[data-bundle-preview-swatches]');
        if (!wrap) return;

        const pos = parseInt(wrap.dataset.stripOptionPosition, 10);
        if (Number.isNaN(pos)) return;

        const val =
          selectedByPosition[pos] ?? variant?.[`option${pos}`];
        if (val == null) return;

        row.querySelectorAll('[data-bundle-strip-selected-label]').forEach((el) => {
          el.hidden = false;
        });
        row.querySelectorAll('[data-bundle-strip-selected-name]').forEach((el) => {
          el.textContent = val;
        });

        const pickSelector = `[data-bundle-swatch-pick][data-preview-value="${CSS.escape(val)}"]`;
        const pick =
          row.querySelector(`${pickSelector}:checked`) ||
          row.querySelector(pickSelector);
        let sourceSwatch = this.getSwatchFromBundlePick(pick);

        if (!sourceSwatch && dialog) {
          const dialogWrap = dialog.querySelector('[data-bundle-preview-swatches]');
          sourceSwatch = this.getSwatchFromBundlePick(
            this.getBundlePickByValue(dialogWrap, val)
          );
        }

        if (!sourceSwatch && dialog) {
          const holder = dialog.querySelector(
            `[data-option-position="${pos}"]`
          );
          if (holder?.tagName === 'FIELDSET') {
            const checked = holder.querySelector('input[type="radio"]:checked');
            sourceSwatch =
              checked?.nextElementSibling?.querySelector('.swatch') ||
              holder.querySelector('[data-selected-value] > .swatch');
          } else if (
            holder?.classList.contains('product-form__input--dropdown')
          ) {
            sourceSwatch = holder.querySelector('[data-selected-value] > .swatch');
          }
        }

        if (sourceSwatch && typeof applySwatchStyles === 'function') {
          row
            .querySelectorAll('[data-bundle-strip-selected-swatch] .swatch')
            .forEach((targetSwatch) => {
              applySwatchStyles(sourceSwatch, targetSwatch);
            });
        }

        this.syncStripSelectedState(row, selectedByPosition);
      }

      committedSelectableCount() {
        return this.querySelectorAll(
          '[data-bundle-row][data-needs-selection="true"][data-committed-variant-id]'
        ).length;
      }

      refreshProgress() {
        const done = this.committedSelectableCount();
        if (this.progressCountEl) {
          this.progressCountEl.textContent = String(done);
        }

        if (this.selectableTotal > 0) {
          const percent = (done / this.selectableTotal) * 100;
          if (this.progressFillEl) {
            this.progressFillEl.style.width = `${percent}%`;
          }
          if (this.progressBarEl) {
            this.progressBarEl.setAttribute('aria-valuenow', String(done));
            this.progressBarEl.setAttribute(
              'aria-valuemax',
              String(this.selectableTotal)
            );
          }
        }

        this.applyResolvedParentVariant();
        this.updateBundleSubmitButtonState(done >= this.selectableTotal);
      }

      canAddBundleToCart() {
        return this.resolveParentVariant() != null;
      }

      updateBundleSubmitButtonState(isBundleReady) {
        const submitBtn = this.getBundleSubmitButton();
        if (!submitBtn || submitBtn.type !== 'submit') return;

        const spans = submitBtn.querySelectorAll('span');
        const labelSpan = spans[0] || null;
        const dividerSpan = spans[1] || null;
        const labelChoose = this.dataset.labelChoose || 'CHOOSE COLORS';
        const labelAdd = this.dataset.labelAdd || 'Add to cart';
        const labelUnavailable =
          this.dataset.labelUnavailable || 'Unavailable';
        const noSelectionRequired = this.selectableTotal <= 0;
        const ready = noSelectionRequired || isBundleReady;
        const parentResolved = this.resolveParentVariant() != null;

        if (!ready || !parentResolved) {
          submitBtn.disabled = true;
          submitBtn.setAttribute('aria-disabled', 'true');
          if (labelSpan) {
            if (ready && !parentResolved) {
              labelSpan.textContent = labelUnavailable;
            } else if (noSelectionRequired) {
              labelSpan.textContent = labelAdd;
            } else {
              labelSpan.textContent = labelChoose;
            }
          }
          if (dividerSpan) dividerSpan.textContent = ' - ';
          if (ready && !parentResolved) {
            console.warn(
              '[product-bundle-sets] No parent bundle variant matches the current selection'
            );
          }
          return;
        }

        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-disabled');
        if (labelSpan) labelSpan.textContent = labelAdd;
        if (dividerSpan) dividerSpan.textContent = ' — ';
      }

      getBundleRowCartLabel(row) {
        if (!row) return '';

        const productTitle =
          row.dataset.productTitle ||
          row.querySelector('.product-bundle-sets__title')?.textContent?.trim() ||
          '';
        const baseTitle = productTitle.replace(/\s—\sShade\s\d+$/i, '').trim();
        const needsSelection = row.getAttribute('data-needs-selection') === 'true';
        const shade = needsSelection ? row.dataset.committedOptionValue || '' : '';

        if (shade && baseTitle) return `${baseTitle} — ${shade}`;
        return baseTitle || shade || '';
      }

      collectBundleCartProperties() {
        const order = [];
        const byVariant = new Map();

        this.querySelectorAll('[data-bundle-row]').forEach((row) => {
          const needsSelection = row.getAttribute('data-needs-selection') === 'true';
          const variantId = needsSelection
            ? row.dataset.committedVariantId
            : row.dataset.defaultVariantId;
          if (!variantId) return;

          const label = this.getBundleRowCartLabel(row);
          if (!label) return;

          const variantKey = String(variantId);
          const existing = byVariant.get(variantKey);
          if (existing) {
            existing.qty += 1;
            return;
          }

          byVariant.set(variantKey, { qty: 1, label });
          order.push(variantKey);
        });

        if (!order.length) return {};

        const totalQty = order.reduce(
          (sum, variantKey) => sum + byVariant.get(variantKey).qty,
          0
        );
        const properties = {
          _total_item: totalQty === 1 ? '1 product' : `${totalQty} products`,
        };

        order.forEach((variantKey, index) => {
          const { qty, label } = byVariant.get(variantKey);
          properties[`_bundle_item_${index + 1}`] = `${qty} x ${label}`;
        });

        return properties;
      }

      getBundleCartStrategy() {
        return (this.dataset.bundleCartStrategy || 'auto').trim().toLowerCase();
      }

      isMultiSlotBundle() {
        const quantitiesRaw = this.dataset.bundleQuantities;
        if (quantitiesRaw) {
          try {
            const quantities = JSON.parse(quantitiesRaw);
            if (Array.isArray(quantities) && quantities.some((qty) => Number(qty) > 1)) {
              return true;
            }
          } catch (e) {
            console.warn('[product-bundle-sets] Invalid bundle quantities JSON', e);
          }
        }

        return this.selectableTotal > 3;
      }

      usesComponentCartStrategy() {
        const strategy = this.getBundleCartStrategy();
        if (strategy === 'parent') return false;
        if (strategy === 'components') return true;
        return this.isMultiSlotBundle();
      }

      getOrderedComponentSelections() {
        const selections = [];

        this.querySelectorAll('[data-bundle-row]').forEach((row) => {
          const needsSelection = row.getAttribute('data-needs-selection') === 'true';
          const variantId = needsSelection
            ? row.dataset.committedVariantId
            : row.dataset.defaultVariantId;
          if (!variantId) return;

          const parsedId = parseInt(String(variantId), 10);
          if (!Number.isFinite(parsedId)) return;

          selections.push({
            variantId: parsedId,
            label: this.getBundleRowCartLabel(row),
            slotIndex: parseInt(row.dataset.bundleSlotIndex, 10) || null,
          });
        });

        return selections;
      }

      getParentVariantRecord(parentId) {
        const config = this.parentVariantConfig || this.getParentVariantConfig();
        const variants = config?.parentVariants || [];
        return variants.find((variant) => String(variant.id) === String(parentId)) || null;
      }

      getBundleCompareAtDollars() {
        const raw = this.dataset.bundleCompareAtDollars;
        if (raw == null || raw === '') return 0;
        const dollars = parseInt(String(raw), 10);
        return Number.isFinite(dollars) ? dollars : 0;
      }

      appendBundleCompareAtProperty(properties, parentVariant) {
        const compareAtDollars = this.getBundleCompareAtDollars();
        const linePriceCents = parentVariant?.price ?? 0;
        const linePriceDollars =
          linePriceCents > 0 ? Math.floor(linePriceCents / 100) : 0;

        if (compareAtDollars > 0 && compareAtDollars > linePriceDollars) {
          properties._bundle_parent_compare_at_dollars = String(compareAtDollars);
          properties._bundle_parent_compare_at_cents = String(
            compareAtDollars * 100
          );
        }

        const parentHandle = this.dataset.parentProductHandle;
        if (parentHandle) {
          properties._bundle_parent_product_handle = String(parentHandle);
        }
      }

      collectComponentLineItems(parentId) {
        const selections = this.getOrderedComponentSelections();
        if (!selections.length) return [];

        const parentProductId = this.dataset.parentProductId || '';
        const parentTitle = this.dataset.parentProductTitle || '';
        const parentImage = this.dataset.parentProductImage || '';
        const parentImageAspectRatio = this.dataset.parentProductImageAspectRatio || '';
        const parentVariant = this.getParentVariantRecord(parentId);
        const bundleGroupId = `${parentId}-${Date.now()}`;
        const componentSpec = selections
          .map((selection) => `${selection.variantId}:1`)
          .join(',');

        const sharedProperties = {
          _bundle_group_id: bundleGroupId,
          _bundle_parent_variant_id: String(parentId),
          _bundle_parent_product_id: String(parentProductId),
          _bundle_parent_title: parentTitle,
          _bundle_component_variants: componentSpec,
        };
        if (parentVariant?.price != null) {
          sharedProperties._bundle_parent_line_price = String(parentVariant.price);
        }
        this.appendBundleCompareAtProperty(sharedProperties, parentVariant);
        if (parentImage) {
          sharedProperties._bundle_parent_image = parentImage;
        }
        if (parentImageAspectRatio) {
          sharedProperties._bundle_parent_image_aspect_ratio = String(parentImageAspectRatio);
        }

        const byVariant = new Map();
        const order = [];

        selections.forEach((selection) => {
          const variantKey = String(selection.variantId);
          const existing = byVariant.get(variantKey);
          if (existing) {
            existing.qty += 1;
            return;
          }

          byVariant.set(variantKey, { qty: 1, variantId: selection.variantId });
          order.push(variantKey);
        });

        return order.map((variantKey) => {
          const { qty, variantId } = byVariant.get(variantKey);
          return {
            id: variantId,
            quantity: qty,
            properties: { ...sharedProperties },
          };
        });
      }

      collectParentLineItems(parentId) {
        const properties = this.collectBundleCartProperties();
        const parentVariant = this.getParentVariantRecord(parentId);
        this.appendBundleCompareAtProperty(properties, parentVariant);
        const item = { id: parentId, quantity: 1 };
        if (Object.keys(properties).length) {
          item.properties = properties;
        }
        return [item];
      }

      collectLineItems() {
        const parentId = this.getResolvedParentVariantId();
        if (parentId == null) return [];

        if (this.usesComponentCartStrategy()) {
          return this.collectComponentLineItems(parentId);
        }

        return this.collectParentLineItems(parentId);
      }

      /**
       * Dev verification: dryRun logs payload; otherwise POSTs to cart/add.js.
       * Usage: document.querySelector('product-bundle-sets')?.verifyBundleCartAdd()
       */
      async verifyBundleCartAdd({ dryRun = true } = {}) {
        const items = this.collectLineItems();
        const payload = {
          items,
          cartStrategy: this.usesComponentCartStrategy() ? 'components' : 'parent',
          resolvedParentVariantId: this.getResolvedParentVariantId(),
          componentVariantIds: this.getOrderedComponentSelections().map(
            (selection) => selection.variantId
          ),
          resolveFailureReason: this.getResolveFailureReason(),
          parentSellable: this.isParentVariantSellable(),
          canAdd: this.canAddBundleToCart(),
          committed: this.committedSelectableCount(),
          selectableTotal: this.selectableTotal,
          selections: this.getRowSelections(),
        };
        if (dryRun) {
          console.info('[product-bundle-sets] verifyBundleCartAdd (dry run)', payload);
          return { ok: payload.canAdd && items.length > 0, payload };
        }
        if (!payload.canAdd || !items.length) {
          return { ok: false, payload, error: 'Bundle not ready or no line items' };
        }
        const root = window.Shopify?.routes?.root || '/';
        const resp = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await resp.json();
        const ok = !data.status && resp.ok;
        console.info('[product-bundle-sets] verifyBundleCartAdd', {
          ok,
          status: resp.status,
          items,
          data,
        });
        return { ok, payload, data };
      }

      async addBundleToCart() {
        const items = this.collectLineItems();
        if (!this.canAddBundleToCart() || !items.length) return;

        const submitBtn = this.getBundleSubmitButton();
        const productForm = this.getBundleProductForm();
        submitBtn?.classList.add('loading');
        submitBtn?.setAttribute('aria-disabled', 'true');
        submitBtn?.setAttribute('disabled', 'disabled');
        productForm?.querySelector('.loading__spinner')?.classList.remove('hidden');

        const cartDrawer = document.querySelector('cart-drawer');
        const body = { items };
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location?.pathname || '';
        }

        try {
          const root = window.Shopify?.routes?.root || '/';
          const addUrl = `${root}cart/add.js`;
          const headers = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          };
          const resp = await fetch(addUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
          });

          const raw = await resp.text();
          let data;
          try {
            data = JSON.parse(raw);
          } catch (parseError) {
            console.error('Bundle add to cart: expected JSON', {
              status: resp.status,
              url: addUrl,
              preview: raw.slice(0, 200),
            });
            return;
          }

          if (data.status) {
            console.error('Bundle add to cart', data);
            return;
          }

          if (cartDrawer && data.sections && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(data);
            if (cartDrawer.classList.contains('is-empty'))
              cartDrawer.classList.remove('is-empty');
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-bundle-sets',
              cartData: data,
            });
          }
        } catch (e) {
          console.error(e);
        } finally {
          const submitBtn = this.getBundleSubmitButton();
          const productForm = this.getBundleProductForm();
          submitBtn?.classList.remove('loading');
          productForm?.querySelector('.loading__spinner')?.classList.add('hidden');
          this.refreshProgress();
        }
      }

      trapFocus(dialog) {
        const first =
          dialog.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        first?.focus?.();
      }
    }
  );
}
